import React from 'react';
import UserAvatar from './UserAvatar';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';

const Timeline = ({ activities, users, onUserAvatarClick }) => {
    const getUserById = (userId) => {
        return users.find(u => u.id === userId);
    };

    const renderActivityMessage = (activity) => {
        const user = getUserById(activity.userId);
        const userName = user ? user.displayName : 'Usuário desconhecido';

        if (activity.message) {
            return (
                <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:m-0">
                    <ReactMarkdown>{activity.message}</ReactMarkdown>
                </div>
            );
        }

        // Fallback for older entries or special cases like observations
        const renderObservationChange = (action, details) => (
            <div>
                <p>
                    <span className="font-semibold">{userName}</span> {action} as observações.
                </p>
                {details && (
                    <div className="mt-2 text-xs text-slate-600 dark:text-slate-400 border-l-2 border-slate-300 dark:border-slate-600 pl-3 space-y-1">
                        {details.old && (
                            <p><span className="font-medium text-red-500">Antes:</span> {details.old}</p>
                        )}
                        {details.new && (
                            <p><span className="font-medium text-green-500">Depois:</span> {details.new}</p>
                        )}
                    </div>
                )}
            </div>
        );

        switch (activity.activityType) {
            case 'create_observation':
                return renderObservationChange('adicionou', activity.details);
            case 'update_observation':
                return renderObservationChange('atualizou', activity.details);
            case 'delete_observation':
                return renderObservationChange('removeu', activity.details);
            default:
                return <p><span className="font-semibold">{userName}</span> realizou uma ação desconhecida: {activity.activityType} em {activity.target}.</p>;
        }
    };

    if (activities.length === 0) {
        return (
            <div className="text-center py-10">
                <p className="text-slate-500 dark:text-slate-400">Nenhuma atividade registrada ainda.</p>
            </div>
        );
    }

    return (
        <div className="relative pl-8 pr-4 py-4">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700"></div>
            <ul className="list-none p-0 m-0">
                {activities.map((activity, index) => {
                    const user = getUserById(activity.userId);
                    const isLastItem = index === activities.length - 1;
                    return (
                        <li
                            key={activity.id || index}
                            className={!isLastItem ? 'pb-8' : ''}
                        >
                            <div
                                className="relative group cursor-pointer outline-none"
                                onClick={() => onUserAvatarClick(user)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onUserAvatarClick(user);
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label={`Ver perfil de ${user ? user.displayName : 'Usuário desconhecido'}`}
                            >
                                <div className="absolute -left-3.5 top-1">
                                    <div
                                        className="h-8 w-8 rounded-full bg-slate-400 dark:bg-slate-600 flex items-center justify-center ring-8 ring-white dark:ring-slate-900 group-focus:ring-blue-100 dark:group-focus:ring-blue-900/50 transition-all"
                                    >
                                        <UserAvatar user={user} size="32" />
                                    </div>
                                </div>
                                <div className="ml-10 rounded-lg group-focus:ring-2 group-focus:ring-blue-500 group-focus:ring-offset-2 dark:group-focus:ring-offset-slate-900 transition-all">
                                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 shadow-sm group-hover:bg-slate-100 dark:group-hover:bg-slate-800 transition-colors">
                                        {renderActivityMessage(activity)}
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        {format(parseISO(activity.timestamp), "d 'de' MMMM 'de' yyyy, 'às' HH:mm", { locale: ptBR })}
                                    </p>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default Timeline;
